import React from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import store from './app/store'
import { validateDomain } from 'domainControl'
import Root from './Root'
import './assets/styles/main.css'
import 'bootstrap/dist/css/bootstrap.min.css'
import './features/forms/forms.css'
import './features/highcharts/highcharts.css'
import { DEFAULT_APP_HOSTNAME, DEFAULT_APP_PROTOCOL } from 'modules/constants'
import { baseUrl } from 'modules/urls'
import ReactHighcharts from 'react-highcharts'

// Import utils early to register Shopify session token axios interceptor
// This must be imported before any axios calls are made
import 'modules/utils'
import { installDevPersonaUrlCapture } from 'modules/devPermissionPersona'
import { syncDevPersonaToRedux } from 'actions/rootActions'

// Disable Highcharts accessibility module warning
ReactHighcharts.Highcharts.setOptions({
  accessibility: {
    enabled: false
  }
})

// Shopify marketplace install: redirect to API for auth; avoid loop when returning from backend (from_shopify_auth=1).
// Runs on every page load with a query string (critical path before React mounts); keep logic minimal.
function isShopifyInstallRedirect(): boolean {
  if (!location.search) return false
  const params = new URLSearchParams(location.search)
  const shop = params.get('shop')
  const hmac = params.get('hmac')
  return !!(hmac && shop && /^([a-zA-Z0-9\-]+)\.myshopify\.com$/.test(shop))
}

function isShopifyReturnFromAuth(): boolean {
  if (!location.search) return false
  const params = new URLSearchParams(location.search)
  return params.get('from_shopify_auth') === '1' && !!params.get('shop')
}

function stripShopifyAuthFlagFromUrl(): void {
  const params = new URLSearchParams(location.search)
  if (params.get('from_shopify_auth') !== '1') return
  params.delete('from_shopify_auth')
  const nextSearch = params.toString()
  const url = nextSearch ? `${location.pathname}?${nextSearch}` : location.pathname
  history.replaceState(null, '', url)
}

// After returning from Shopify's hosted plan page (App Pricing), land on the Plan
// page — the merchant was managing their plan. The flag is set right before we
// redirect out (planActions) and consumed once here, before the router mounts, so
// a normal load is unaffected.
function applyPostBillingRoute(): void {
  try {
    if (localStorage.getItem('payhelm_return_to_plan') !== '1') return
    localStorage.removeItem('payhelm_return_to_plan')
    if (!location.pathname.startsWith('/plan')) history.replaceState(null, '', `/plan${location.search}`)
  } catch { /* localStorage unavailable — no-op */ }
}

if (isShopifyInstallRedirect() && !isShopifyReturnFromAuth()) {
  location.replace(`${baseUrl}/auth/shopify${location.search}`)
} else if (location.hostname === DEFAULT_APP_HOSTNAME && location.protocol !== DEFAULT_APP_PROTOCOL) {
  location.href = location.href.replace(location.protocol, DEFAULT_APP_PROTOCOL)
} else if (validateDomain()) {
  if (isShopifyReturnFromAuth()) stripShopifyAuthFlagFromUrl()
  applyPostBillingRoute()
  installDevPersonaUrlCapture(() => {
    store.dispatch(syncDevPersonaToRedux())
  })
  class Index extends React.Component {
    constructor(props) {
      super(props)
    }

    render() {
      return (
        <Provider store={store}>
          <Root />
        </Provider>
      )
    }
  }

  const container = document.getElementById('sub-analytics-root')
  const root = createRoot(container)
  root.render(React.createElement(Index))
  // render(React.createElement(Index), document.getElementById(''))
}